import { useEffect, useMemo, useState } from 'react';

import { useAppSelector } from '../store';
import type { UserStoreType } from '../types';
import type { Fan } from '../types/idp-types';
import { getCurrentUser, isUserLogged, waitForIdp } from '../utils/IdpUtils';

export const useUser = () => {
  const reduxUser = useAppSelector((state) => state.user);
  const [isIdpReady, setIsIdpReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [idpUser, setIdpUser] = useState<Fan | null>();

  useEffect(() => {
    let cancelled = false;

    const fetchUser = async () => {
      await waitForIdp();
      if (cancelled) return;

      setIsIdpReady(true);

      const isLogged = await isUserLogged();
      if (cancelled) return;

      setIsAuthenticated(Boolean(isLogged));

      if (isLogged) {
        const result = await getCurrentUser();
        if (cancelled) return;

        if (result.success && result.value) {
          setIdpUser(result.value);
        }
      }
    };

    fetchUser();

    return () => {
      cancelled = true;
    };
  }, []);

  const user: UserStoreType = useMemo(() => {
    if (!isIdpReady) {
      return reduxUser;
    }

    if (isAuthenticated) {
      return {
        ...reduxUser,
        idProvider: 'PING',
        userId: idpUser?.sub ?? reduxUser.userId,
        loginType: 'idp',
        isLogged: true,
      };
    }

    return {
      ...reduxUser,
      idProvider: 'ANONYMOUS',
      isLogged: false,
    };
  }, [idpUser?.sub, isAuthenticated, isIdpReady, reduxUser]);

  return {
    user,
    isIdpReady,
    isAuthenticated,
  };
};
